import { deleteNodeApi, getParentPath } from 'domains/IngredientsTree/helpers'
import { useCallback, useMemo, useState } from 'react'

import { message } from 'antd'
import { useIngredientContext } from 'contexts/Ingredient/IngredientProvider'
import { useTranslations } from 'contexts/Translation'
import { useUser } from 'domains/User/context'

/**
 * Hook to delete an ingredient tree node via backend API with optimistic UI.
 *
 * Flow is aligned with create/update:
 * 1) Optimistically update context tree and set loading
 * 2) Call backend
 * 3) On success: clear optimistic tree and resync from RDB
 * 4) On error: rollback optimistic tree and resync from RDB
 */
const useDeleteIngredientNode = ({ onUpdateCurrentTreeLevel }) => {
  const { t } = useTranslations()
  const { user } = useUser()
  const { ingredientsTree, dispatch } = useIngredientContext()
  const [loading, setLoading] = useState(false)

  /**
   * Optimistically remove node and promote its children to the parent container
   * to mirror current backend delete behavior.
   */
  const removeNodeOptimistically = useCallback(
    (node) => {
      if (!ingredientsTree || !node?._path) return ingredientsTree

      const parentPath = getParentPath(node._path)
      const updatedTree = JSON.parse(JSON.stringify(ingredientsTree))

      const getNodeByPath = (tree, path) => {
        if (!path) return { node: null }
        const parts = path.split('/').slice(1) // drop root key
        let current = tree
        let nodeRef = null
        for (const part of parts) {
          if (part === 'children') continue
          nodeRef = current?.[part] || null
          current = nodeRef?.children || null
        }
        return { node: nodeRef }
      }

      // Parent node and container
      const parentNode = parentPath
        ? getNodeByPath(updatedTree, parentPath).node
        : null
      const parentContainer = parentPath ? parentNode?.children : updatedTree

      if (!parentContainer) return ingredientsTree

      const nodeId = node._id
      const nodeChildren = node.children || {}

      // Merge children into parent container (promote)
      const nextContainer = {
        ...(parentContainer || {}),
        ...(nodeChildren || {})
      }
      delete nextContainer[nodeId]

      // If deleting root and promoting children, mark them top-level
      if (!parentPath) {
        Object.values(nodeChildren || {}).forEach((child) => {
          child.isTopLevel = true
        })
      }

      // Assign back to parent
      if (parentPath && parentNode) {
        parentNode.children = nextContainer
        if (!Object.keys(nextContainer || {}).length) {
          parentNode.isLastLevel = true
        }
      } else {
        Object.keys(updatedTree).forEach((key) => delete updatedTree[key])
        Object.assign(updatedTree, nextContainer)
      }

      return updatedTree
    },
    [ingredientsTree]
  )

  const handleDeleteNode = useCallback(
    async (node) => {
      if (!node?._id || !node?._path) return

      const parentPath = getParentPath(node._path)

      // Optimistic update
      if (ingredientsTree) {
        try {
          const optimisticTree = removeNodeOptimistically(node)
          dispatch({ type: 'SET_OPTIMISTIC_TREE', payload: optimisticTree })
        } catch (error) {
          console.error('Failed to apply optimistic delete', error)
        }
      }

      setLoading(true)
      dispatch({ type: 'SET_LOADING', payload: true })

      try {
        const result = await deleteNodeApi({
          _id: node._id,
          _path: node._path,
          parentPath: parentPath || null,
          productsIds: node?.productsIds || [],
          children: node?.children || null,
          userId: user?._id
        })

        dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
        await onUpdateCurrentTreeLevel(null, parentPath)

        if (result.code === 'OK' && result.productsSync?.fullyUpdated) {
          message.success(t('Ingredient successfully deleted'))
        } else if (result.code === 'NODE_DELETED_PRODUCTS_PARTIALLY_UPDATED') {
          const failedCount = result.productsSync?.failedProducts?.length || 0
          const totalPlanned = result.productsSync?.totalProductsPlanned || 0
          const totalUpdated = result.productsSync?.totalProductsUpdated || 0

          message.success(t('Ingredient successfully deleted'))
          message.warning(
            t('Some products were not updated:') +
              `${totalUpdated} of ${totalPlanned} products updated` +
              (failedCount > 0 ? ` (${failedCount} failed)` : ''),
            5
          )
        } else {
          message.success(t('Ingredient successfully deleted'))
        }
      } catch (error) {
        console.error('Failed to delete node', error)
        dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
        dispatch({ type: 'SET_LOADING', payload: false })

        // Resync to actual state
        try {
          await onUpdateCurrentTreeLevel(null, parentPath, false)
        } catch (syncError) {
          console.error('Failed to sync tree after delete error', syncError)
        }

        if (error.code === 'TREE_UPDATE_FAILED') {
          message.error(
            t(
              'failedToUpdateIngredientTree',
              'Failed to update ingredient tree. Please try again.'
            )
          )
        } else {
          message.error(
            error.message || t('Something went wrong during delete')
          )
        }
        throw error
      } finally {
        setLoading(false)
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    },
    [
      dispatch,
      ingredientsTree,
      onUpdateCurrentTreeLevel,
      removeNodeOptimistically,
      t,
      user?._id
    ]
  )

  return useMemo(
    () => ({
      handleDeleteNode,
      loading
    }),
    [handleDeleteNode, loading]
  )
}

export default useDeleteIngredientNode
